"use client";

import {
  AuiIf,
  ThreadListItemMorePrimitive,
  ThreadListItemPrimitive,
  ThreadListPrimitive,
  useAui,
  useAuiState,
} from "@assistant-ui/react";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  ChevronRightIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PinIcon,
  PinOffIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
  XIcon,
} from "lucide-react";
import {
  type ComponentPropsWithoutRef,
  type FC,
  Fragment,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/atoms/button";
import { Skeleton } from "@/components/ui/atoms/skeleton";
import { useApi } from "@/lib/api-client";
import { isPinned, type ThreadDto } from "@/lib/threads";
import { cn } from "@/lib/utils";

export const ThreadListRoot: FC<ComponentPropsWithoutRef<typeof ThreadListPrimitive.Root>> = ({
  className,
  ...props
}) => {
  return (
    <ThreadListPrimitive.Root
      data-slot="aui_thread-list-root"
      className={cn("flex flex-col gap-0.5", className)}
      {...props}
    />
  );
};

/** The chats: a search box, pinned and dated groups, and the archived chats at the bottom. */
export const ThreadListItems: FC<ComponentPropsWithoutRef<"div">> = ({ className, ...props }) => {
  const [query, setQuery] = useState("");
  const searching = query.trim().length > 0;
  return (
    <div
      data-slot="aui_thread-list-items"
      className={cn("flex flex-col gap-0.5", className)}
      {...props}
    >
      <ThreadSearchInput value={query} onChange={setQuery} />
      {searching ? (
        <ThreadSearchResults query={query.trim()} onOpen={() => setQuery("")} />
      ) : (
        <>
          <AuiIf condition={(s) => s.threads.isLoading}>
            <ThreadListSkeleton />
          </AuiIf>
          <AuiIf condition={(s) => !s.threads.isLoading}>
            <ThreadListItemGroups />
            <ArchivedThreads />
          </AuiIf>
        </>
      )}
    </div>
  );
};

const ThreadSearchInput: FC<{ value: string; onChange: (value: string) => void }> = ({
  value,
  onChange,
}) => {
  return (
    <div className="relative mb-1 flex items-center">
      <SearchIcon className="text-muted-foreground pointer-events-none absolute start-2.5 size-3.5" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onChange("");
        }}
        placeholder="Search chats"
        aria-label="Search chats"
        maxLength={200}
        className="placeholder:text-muted-foreground focus-visible:ring-ring/50 hover:bg-accent focus:bg-accent h-8 w-full rounded-md bg-transparent ps-8 pe-7 text-[13px] outline-none focus-visible:ring-[3px] [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear search"
          className="text-muted-foreground hover:text-foreground absolute end-2 flex size-4 items-center justify-center"
        >
          <XIcon className="size-3.5" />
        </button>
      )}
    </div>
  );
};

/** Searches past chats (title today, message text once indexed) as the user types. */
const ThreadSearchResults: FC<{ query: string; onOpen: () => void }> = ({ query, onOpen }) => {
  const client = useApi();
  const aui = useAui();
  const [results, setResults] = useState<ThreadDto[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await client.api.chat.threads.search.$get({ query: { q: query } });
        const rows = res.ok ? ((await res.json()) as ThreadDto[]) : [];
        if (!cancelled) setResults(rows);
      } catch {
        if (!cancelled) setResults([]);
      }
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [client, query]);

  if (results === null) return <ThreadListSkeleton count={2} />;
  if (results.length === 0) {
    return <p className="text-muted-foreground px-2.5 py-2 text-[13px]">No chats match.</p>;
  }
  return (
    <ul aria-label="Search results" className="flex flex-col gap-0.5">
      {results.map((thread) => (
        <li key={thread.id}>
          <button
            type="button"
            onClick={() => {
              aui.threads().switchToThread(thread.id);
              onOpen();
            }}
            className="hover:bg-accent focus-visible:ring-ring/50 text-foreground/80 flex h-8 w-full items-center gap-2 rounded-md px-2.5 text-start text-[13px] outline-none focus-visible:ring-[3px]"
          >
            <span className="min-w-0 flex-1 truncate">{thread.title}</span>
            {thread.archived_at && (
              <span className="text-muted-foreground shrink-0 text-[11px]">Archived</span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
};

/** "Archived" at the bottom of the list: collapsed by default, restore or delete from there. */
const ArchivedThreads: FC = () => {
  const count = useAuiState((s) => s.threads.archivedThreadIds.length);
  const [open, setOpen] = useState(false);
  if (count === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-0.5">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="text-muted-foreground hover:text-foreground flex items-center gap-1 px-2.5 pt-1 pb-1 text-xs"
      >
        <ChevronRightIcon className={cn("size-3 transition-transform", open && "rotate-90")} />
        Archived · {count}
      </button>
      {open && (
        <ThreadListPrimitive.Items archived>
          {() => <ThreadListItem archived />}
        </ThreadListPrimitive.Items>
      )}
    </div>
  );
};

const DAY_IN_MS = 86_400_000;

const dateGroupLabel = (date: Date | undefined, startOfToday: number): string => {
  if (!date || date.getTime() >= startOfToday) return "Today";
  if (date.getTime() >= startOfToday - DAY_IN_MS) return "Yesterday";
  return "Earlier";
};

type ThreadListGroup = { label: string; indices: number[] };

const ThreadListItemGroups: FC = () => {
  const threadIds = useAuiState((s) => s.threads.threadIds);
  const threadItems = useAuiState((s) => s.threads.threadItems);

  const groups = useMemo<ThreadListGroup[] | null>(() => {
    const itemsById = new Map(threadItems.map((item) => [item.id, item]));
    const dates = threadIds.map((id) => itemsById.get(id)?.lastMessageAt);
    const pinned = threadIds.map((id) => isPinned(itemsById.get(id)?.custom));
    if (!dates.some(Boolean) && !pinned.some(Boolean)) return null;

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const time = (index: number) => dates[index]?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const all = threadIds.map((_, index) => index);
    // Pinned chats keep the server's order (most recently pinned first); the rest go by date.
    const pinnedIndices = all.filter((index) => pinned[index]);
    const indices = all.filter((index) => !pinned[index]).sort((a, b) => time(b) - time(a));

    const result: ThreadListGroup[] = pinnedIndices.length
      ? [{ label: "Pinned", indices: pinnedIndices }]
      : [];
    for (const index of indices) {
      const label = dateGroupLabel(dates[index], startOfToday);
      const lastGroup = result[result.length - 1];
      if (lastGroup?.label === label) {
        lastGroup.indices.push(index);
      } else {
        result.push({ label, indices: [index] });
      }
    }
    return result;
  }, [threadIds, threadItems]);

  if (!groups) {
    return <ThreadListPrimitive.Items>{() => <ThreadListItem />}</ThreadListPrimitive.Items>;
  }

  return groups.map((group) => (
    <Fragment key={group.label}>
      <div
        data-slot="aui_thread-list-group-label"
        className="text-muted-foreground px-2.5 pt-3 pb-1 text-xs"
      >
        {group.label}
      </div>
      {group.indices.map((index) => (
        <ThreadListPrimitive.ItemByIndex
          key={threadIds[index]}
          index={index}
          components={{ ThreadListItem }}
        />
      ))}
    </Fragment>
  ));
};

export const ThreadListNew = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof Button> & { labelClassName?: string }
>(({ className, labelClassName, children, ...props }, ref) => {
  return (
    <ThreadListPrimitive.New asChild>
      <Button
        ref={ref}
        variant="ghost"
        data-slot="aui_thread-list-new"
        className={cn(
          "hover:bg-accent data-active:bg-accent h-8 justify-start gap-2 rounded-md px-2.5 text-sm font-normal",
          className,
        )}
        {...props}
      >
        {children ?? (
          <>
            <PlusIcon data-slot="aui_thread-list-new-icon" className="size-4 shrink-0" />
            <span
              data-slot="aui_thread-list-new-label"
              className={cn("whitespace-nowrap", labelClassName)}
            >
              New Thread
            </span>
          </>
        )}
      </Button>
    </ThreadListPrimitive.New>
  );
});

ThreadListNew.displayName = "ThreadListNew";

const ThreadListSkeleton: FC<{ count?: number }> = ({ count = 5 }) => {
  return (
    <div className="flex flex-col gap-0.5">
      {Array.from({ length: count }).map(() => {
        const sk = crypto.randomUUID();
        return (
          <div
            key={sk}
            role="status"
            aria-label="Loading threads"
            data-slot="aui_thread-list-skeleton-wrapper"
            className="flex h-8 items-center px-2.5"
          >
            <Skeleton data-slot="aui_thread-list-skeleton" className="h-3.5 w-full" />
          </div>
        );
      })}
    </div>
  );
};

const ThreadListItem: FC<{ archived?: boolean }> = ({ archived = false }) => {
  const [editing, setEditing] = useState(false);
  return (
    <ThreadListItemPrimitive.Root
      data-slot="aui_thread-list-item"
      className="group hover:bg-accent focus-visible:bg-accent data-active:bg-accent has-focus-visible:bg-accent has-data-[state=open]:bg-accent text-foreground/80 data-active:text-foreground data-active:font-medium relative flex h-8 items-center rounded-md transition-colors focus-visible:outline-none"
    >
      {editing ? (
        <ThreadTitleEditor onDone={() => setEditing(false)} />
      ) : (
        <>
          <ThreadListItemPrimitive.Trigger
            data-slot="aui_thread-list-item-trigger"
            className="focus-visible:ring-ring/50 flex h-full min-w-0 flex-1 items-center rounded-md px-2.5 text-start text-[13px] outline-none group-hover:pe-9 group-has-focus-visible:pe-9 group-has-data-[state=open]:pe-9 group-data-active:pe-9 focus-visible:ring-[3px]"
          >
            <span data-slot="aui_thread-list-item-title" className="min-w-0 flex-1 truncate">
              <ThreadListItemPrimitive.Title fallback="New Chat" />
            </span>
          </ThreadListItemPrimitive.Trigger>
          <ThreadListItemMore archived={archived} onRename={() => setEditing(true)} />
        </>
      )}
    </ThreadListItemPrimitive.Root>
  );
};

/** Renames in place: Enter or leaving the field saves, Escape cancels. */
const ThreadTitleEditor: FC<{ onDone: () => void }> = ({ onDone }) => {
  const aui = useAui();
  const title = useAuiState((s) => s.threadListItem.title) ?? "";
  const [value, setValue] = useState(title);
  const input = useRef<HTMLInputElement>(null);
  const finished = useRef(false);

  useEffect(() => {
    // After the menu has closed and handed focus back to its trigger.
    const frame = requestAnimationFrame(() => input.current?.select());
    return () => cancelAnimationFrame(frame);
  }, []);

  const finish = (save: boolean) => {
    if (finished.current) return;
    finished.current = true;
    const next = value.trim().slice(0, 80);
    if (save && next && next !== title) aui.threadListItem().rename(next);
    onDone();
  };

  return (
    <input
      ref={input}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => finish(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter") finish(true);
        if (e.key === "Escape") finish(false);
      }}
      aria-label="Chat title"
      maxLength={80}
      className="focus-visible:ring-ring/50 bg-background h-full w-full rounded-md px-2.5 text-[13px] outline-none focus-visible:ring-[3px]"
    />
  );
};

const menuItem =
  "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm outline-none select-none";

const ThreadListItemMore: FC<{ archived: boolean; onRename: () => void }> = ({
  archived,
  onRename,
}) => {
  const aui = useAui();
  const custom = useAuiState((s) => s.threadListItem.custom);
  const pinned = isPinned(custom);
  return (
    <ThreadListItemMorePrimitive.Root sharedFocusGroup>
      <ThreadListItemMorePrimitive.Trigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-slot="aui_thread-list-item-more"
          className="data-[state=open]:bg-accent absolute end-1.5 top-1/2 size-6 -translate-y-1/2 p-0 opacity-0 group-hover:opacity-100 group-has-focus-visible:opacity-100 group-data-active:opacity-100 data-[state=open]:opacity-100"
        >
          <MoreHorizontalIcon className="size-3.5" />
          <span className="sr-only">More options</span>
        </Button>
      </ThreadListItemMorePrimitive.Trigger>
      <ThreadListItemMorePrimitive.Content
        side="right"
        align="start"
        sideOffset={6}
        data-slot="aui_thread-list-item-more-content"
        className="bg-popover/95 text-popover-foreground data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:animate-out data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-32 overflow-hidden rounded-xl border p-1.5 shadow-lg backdrop-blur-sm"
      >
        <ThreadListItemMorePrimitive.Item
          data-slot="aui_thread-list-item-more-item"
          className={menuItem}
          onSelect={onRename}
        >
          <PencilIcon className="size-4" />
          Rename
        </ThreadListItemMorePrimitive.Item>
        {archived ? (
          <ThreadListItemPrimitive.Unarchive asChild>
            <ThreadListItemMorePrimitive.Item
              data-slot="aui_thread-list-item-more-item"
              className={menuItem}
            >
              <ArchiveRestoreIcon className="size-4" />
              Restore
            </ThreadListItemMorePrimitive.Item>
          </ThreadListItemPrimitive.Unarchive>
        ) : (
          <>
            <ThreadListItemMorePrimitive.Item
              data-slot="aui_thread-list-item-more-item"
              className={menuItem}
              onSelect={() => aui.threadListItem().updateCustom({ ...custom, pinned: !pinned })}
            >
              {pinned ? <PinOffIcon className="size-4" /> : <PinIcon className="size-4" />}
              {pinned ? "Unpin" : "Pin"}
            </ThreadListItemMorePrimitive.Item>
            <ThreadListItemPrimitive.Archive asChild>
              <ThreadListItemMorePrimitive.Item
                data-slot="aui_thread-list-item-more-item"
                className={menuItem}
              >
                <ArchiveIcon className="size-4" />
                Archive
              </ThreadListItemMorePrimitive.Item>
            </ThreadListItemPrimitive.Archive>
          </>
        )}
        <ThreadListItemPrimitive.Delete asChild>
          <ThreadListItemMorePrimitive.Item
            data-slot="aui_thread-list-item-more-item"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive focus:bg-destructive/10 focus:text-destructive flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm outline-none select-none"
          >
            <TrashIcon className="size-4" />
            Delete
          </ThreadListItemMorePrimitive.Item>
        </ThreadListItemPrimitive.Delete>
      </ThreadListItemMorePrimitive.Content>
    </ThreadListItemMorePrimitive.Root>
  );
};
